// Sector and system labels (spec 15c §3.2). System labels keep their on-screen size: each sits in a
// box scaled by 1 / camera scale, so its children are laid out in screen pixels below the dot.
// Labels that would overlap a higher-priority one stay hidden (15c feedback, `declutter.ts`).
import type { MapResponse, MapSystem } from '@stars-and-ships/api-types';
import { Container, Text } from 'pixi.js';
import { MAP_COLORS } from '../../theme';
import { type Candidate, labelKey, type Offset, PRIORITY, sectorKey } from '../declutter';
import { labelLines } from '../markers';
import { dotRadiusPx, type ZoomTier } from '../zoomTier';

/** Pixels between a system's dot and its label. */
export const LABEL_GAP_PX = 4;
export const LABEL_FONT = 'system-ui, sans-serif';
export const LABEL_STROKE = { color: 0x050914, width: 3 };
const SECTOR_PX = 13;

export interface LabelsLayer {
  container: Container;
  setView(tier: ZoomTier, scale: number): void;
  /** Screen rectangles of the labels the tier shows, set by the last setView. */
  candidates(priority: (system: MapSystem) => number): Candidate[];
  /** Hides the labels whose key is not in `shown`. */
  applyShown(shown: ReadonlyMap<string, Offset>): void;
}

export function labelsLayer(input: {
  map: MapResponse;
  playerId: string | undefined;
}): LabelsLayer {
  const container = new Container();
  const entries = input.map.systems.map((system) => {
    const own = input.playerId !== undefined && system.owner?.playerId === input.playerId;
    const box = new Container();
    box.position.set(system.x, system.y);
    const title = new Text({
      text: '',
      style: {
        fill: own ? MAP_COLORS.own : MAP_COLORS.label,
        fontSize: 12,
        fontFamily: LABEL_FONT,
        stroke: LABEL_STROKE,
      },
    });
    title.anchor.set(0.5, 0);
    const owner = new Text({
      text: '',
      style: {
        fill: MAP_COLORS.labelFaint,
        fontSize: 11,
        fontFamily: LABEL_FONT,
        stroke: LABEL_STROKE,
      },
    });
    owner.anchor.set(0.5, 0);
    box.addChild(title, owner);
    container.addChild(box);
    return { system, box, title, owner, inTier: false };
  });
  // Sector names last, so they are never covered; a constant 13 px, hidden at the close tier.
  const sectors = new Container();
  const sectorLabels: { key: string; label: Text; y: number }[] = [];
  for (const sector of input.map.sectors) {
    const label = new Text({
      text: sector.name,
      style: {
        fill: MAP_COLORS.sector,
        fontSize: 28,
        fontFamily: LABEL_FONT,
        stroke: { color: 0x050914, width: 6 },
      },
    });
    label.anchor.set(0.5);
    label.alpha = 0.9;
    label.position.set(sector.centerX, sector.centerY - sector.radius - 1.5);
    sectors.addChild(label);
    sectorLabels.push({ key: sectorKey(sector.id), label, y: label.y });
  }
  container.addChild(sectors);
  let shown: ZoomTier | null = null;
  let viewScale = 1;
  return {
    container,
    setView(tier, scale) {
      viewScale = scale;
      sectors.visible = tier !== 'close';
      for (const label of sectors.children) label.scale.set(SECTOR_PX / 28 / scale);
      const top = dotRadiusPx(scale) + LABEL_GAP_PX;
      for (const e of entries) {
        e.box.scale.set(1 / scale);
        e.title.y = top;
        e.owner.y = top + 14;
      }
      if (tier === shown) return;
      shown = tier;
      for (const e of entries) {
        const lines = labelLines(e.system, tier, input.playerId);
        e.title.text = lines.title ?? '';
        e.owner.text = lines.owner ?? '';
        e.owner.visible = lines.owner !== null;
        e.inTier = lines.title !== null;
        e.box.visible = e.inTier;
      }
    },
    candidates(priority) {
      const out: Candidate[] = [];
      if (sectors.visible) {
        for (const { key, label, y: baseY } of sectorLabels) {
          // A sector label's size is in world units; its anchor is the centre. It may move a line
          // up or down to get clear of the systems on its rim.
          const w = label.width * viewScale;
          const h = label.height * viewScale;
          const x = label.x * viewScale - w / 2;
          const y = baseY * viewScale - h / 2;
          out.push({
            key,
            priority: PRIORITY.sector,
            rects: [{ x, y, w, h }],
            offsets: [
              { dx: 0, dy: 0 },
              { dx: 0, dy: -h },
              { dx: 0, dy: h },
            ],
          });
        }
      }
      for (const e of entries) {
        if (!e.inTier) continue;
        // The box children are in screen pixels around the system's point.
        const w = Math.max(e.title.width, e.owner.visible ? e.owner.width : 0);
        const bottom = e.owner.visible ? e.owner.y + e.owner.height : e.title.y + e.title.height;
        const x = e.system.x * viewScale - w / 2;
        const y = e.system.y * viewScale + e.title.y;
        out.push({
          key: labelKey(e.system.id),
          systemId: e.system.id,
          priority: priority(e.system),
          rects: [{ x, y, w, h: bottom - e.title.y }],
        });
      }
      return out;
    },
    applyShown(shown) {
      for (const { key, label, y } of sectorLabels) {
        const offset = shown.get(key);
        label.visible = offset !== undefined;
        if (offset) label.y = y + offset.dy / viewScale;
      }
      // System labels always sit below their dot (Tim, round 3: moving labels jumped on zoom).
      for (const e of entries) e.box.visible = e.inTier && shown.has(labelKey(e.system.id));
    },
  };
}
